import { timelineSteps } from "@/mocks/homeData";
import { SectionHead } from "./kit";

const stateStyle = {
  done: { dot: "bg-secondary-500", ring: "ring-secondary-100", label: "已复盘", labelCls: "bg-secondary-100 text-secondary-900" },
  active: { dot: "bg-accent-500", ring: "ring-accent-100", label: "进行中", labelCls: "bg-accent-100 text-accent-800" },
  open: { dot: "border-2 border-foreground-300 bg-background-50", ring: "ring-foreground-100", label: "待核实", labelCls: "bg-foreground-100 text-foreground-600" },
} as const;

export default function TimelineBlock() {
  return (
    <section id="timeline" className="mx-auto max-w-6xl px-4 py-7 md:px-6 md:py-10">
      <SectionHead
        kicker="时间线 · TIMELINE"
        title="一桩事是怎么走到今天的"
        tone="accent"
        extra={
          <button
            type="button"
            className="hidden items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50 px-3.5 py-1.5 text-sm font-semibold text-accent-700 whitespace-nowrap transition-colors hover:bg-accent-100 md:inline-flex"
          >
            查看完整 47 节点
            <i className="ri-arrow-right-line" />
          </button>
        }
      />

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
        {/* case header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 bg-gradient-to-r from-primary-950 to-primary-900 px-5 py-4 text-background-50 md:px-6">
          <div>
            <p className="font-label text-[11px] font-bold tracking-[0.2em] text-accent-400">档案编号 · HM-2026-0413</p>
            <h3 className="mt-1 font-heading text-lg font-bold md:text-xl">「排队神话」茶饮连锁始末</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-full bg-background-50/10 px-2.5 py-1">
              <i className="ri-time-line text-accent-400" />跨度 4 周 · 5 大阶段
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-background-50/10 px-2.5 py-1">
              <i className="ri-file-list-3-line text-secondary-300" />证据条目 38 条
            </span>
          </div>
        </div>

        <div className="grid gap-0 lg:grid-cols-[1fr_1.6fr]">
          {/* summary panel */}
          <div className="hidden flex-col justify-between gap-6 border-r border-background-200 bg-background-50 p-6 lg:flex md:p-8">
            <div>
              <p className="font-heading text-[15px] font-bold text-foreground-950">为什么做这期</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                一场爆红往往只在几天内完成，但问题的种子早就埋下。我们把热度散尽后没人再整理的部分拾起来，按时间顺序把每个节点的证据放回原位，让旁观的人能顺着线头自己看明白。
              </p>
            </div>
            <div className="rounded-lg border border-accent-200 bg-accent-50 p-4">
              <p className="mb-2 flex items-center gap-1.5 font-label text-[11px] font-bold tracking-[0.14em] text-accent-800">
                <i className="ri-alert-line" />
                阅读提示
              </p>
              <p className="text-[13px] leading-relaxed text-accent-900/90">
                时间线只做「证据到哪写到哪」，尚未核实处会明确标注为待核实，不替读者下结论。
              </p>
            </div>
          </div>

          {/* steps rail */}
          <div className="px-5 py-5 md:px-8 md:py-8">
            <ol className="relative space-y-7 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-background-300 md:before:left-[9px]">
              {timelineSteps.map((s, i) => {
                const st = stateStyle[s.state];
                return (
                  <li key={s.phase} className="relative pl-9">
                    <span
                      className={`absolute left-0 top-1.5 flex h-4 w-4 items-center justify-center rounded-full ring-4 md:h-5 md:w-5 ${st.dot} ${st.ring}`}
                    />
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="font-label text-xs font-bold tracking-[0.18em] text-foreground-400">
                          第 {String(i + 1).padStart(2, "0")} 阶段
                        </span>
                        <span className="rounded bg-background-200 px-2 py-0.5 text-xs font-semibold text-foreground-700">
                          {s.phase}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${st.labelCls}`}>
                          {st.label}
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-calendar-line" />
                        {s.date}
                      </span>
                    </div>
                    <h4 className="mt-2 font-heading text-base font-bold text-foreground-950 md:text-lg">{s.title}</h4>
                    <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-foreground-600">{s.desc}</p>
                    <span className="mt-2.5 inline-flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-400">
                      <i className="ri-folder-open-line text-secondary-600" />
                      佐证材料：{s.evidence}
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}